import * as React from 'react';
import QuickPinchZoom, { make3dTransformValue } from 'react-quick-pinch-zoom';
import { useSpringCarousel } from 'react-spring-carousel';

import { Video } from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';
import { ResizableImagePlaceholder } from '@containers/ProductDetailContainer/ProductDetail/ProductImageGallery/ImageSlider/ResizableImagePlaceholder';
import { getVideoIframe } from '@containers/ProductDetailContainer/utils';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { IImageProperties } from '@helpers/utils';

import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';
import { ModiFaceOverlay } from '../ModiFaceOverlay';

import { ProgressBar } from './ProgressBar';
import {
  ImageSliderWrapper,
  PlayerWrapper,
  StyledArrow,
  ZoomWrapper,
} from './styled';
import { useTrackSwiping } from './useTrackSwiping';

export interface IImageSliderProps {
  imagesAttrs: IImageProperties[];
  videos?: Video[];
  hasModiface?: boolean;
  openModifaceModal?: () => void;
}

export const ImageSlider: React.FC<IImageSliderProps> = React.memo(
  ({ imagesAttrs, videos = [], hasModiface, openModifaceModal }) => {
    const newPdDesign = useNewPdDesignEnabled();
    const { trackSwiping } = useTrackSwiping();
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const zoomableElementsRef = React.useRef<HTMLDivElement[]>([]);
    const zoomControllersRef = React.useRef<QuickPinchZoom[]>([]);

    const handleZoom = React.useCallback(
      ({ x, y, scale }, el: HTMLDivElement) => {
        if (el) {
          const value = make3dTransformValue({ x, y, scale });
          el.style.setProperty('transform', value);
        }
      },
      []
    );

    const items: Array<{ id: string; renderItem: JSX.Element }> = React.useMemo(
      () => [
        ...imagesAttrs
          .map((imgProps, index) => {
            const showModifaceOverlay = hasModiface && index === 0;
            const zoomItemIndex = hasModiface ? index + 1 : index;

            const onZoomUpdate = (params) =>
              handleZoom(params, zoomableElementsRef.current[zoomItemIndex]);
            const handleZoomRef = (ref) =>
              (zoomControllersRef.current[zoomItemIndex] = ref);
            const handleDivRef = (el) =>
              (zoomableElementsRef.current[zoomItemIndex] = el);

            // if modiface overlay on mobile, clone main image so customer can swipe to image a view it without overlay
            if (showModifaceOverlay) {
              return [
                {
                  id: `${imgProps.src}-modiface`,
                  renderItem: (
                    <ResizableImagePlaceholder
                      initialInView={true}
                      imgProps={imgProps}
                    >
                      <ModiFaceOverlay onClick={openModifaceModal} />
                    </ResizableImagePlaceholder>
                  ),
                },
                {
                  id: imgProps.src,
                  renderItem: (
                    <Zoomable
                      onUpdate={onZoomUpdate}
                      zoomRef={handleZoomRef}
                      divRef={handleDivRef}
                    >
                      <ResizableImagePlaceholder imgProps={imgProps} />
                    </Zoomable>
                  ),
                },
              ];
            }

            return {
              id: imgProps.src,
              renderItem: (
                <Zoomable
                  onUpdate={onZoomUpdate}
                  zoomRef={handleZoomRef}
                  divRef={handleDivRef}
                >
                  <ResizableImagePlaceholder
                    initialInView={index === 0}
                    imgProps={imgProps}
                  />
                </Zoomable>
              ),
            };
          })
          .flat(),

        ...videos.map((video) => ({
          id: video.id,
          renderItem: (
            <PlayerWrapper
              newPdDesign={newPdDesign}
              dangerouslySetInnerHTML={{
                __html: getVideoIframe(video),
              }}
            />
          ),
        })),
      ],
      [
        hasModiface,
        imagesAttrs,
        newPdDesign,
        openModifaceModal,
        handleZoom,
        videos,
      ]
    );

    const {
      carouselFragment,
      useListenToCustomEvent,
      slideToNextItem,
      slideToPrevItem,
    } = useSpringCarousel({
      items,
    });

    useListenToCustomEvent((event) => {
      if (event.eventName === 'onDrag') {
        trackSwiping('swipe');
      } else if (event.eventName === 'onSlideStartChange') {
        // reset zoom in slide change
        zoomControllersRef.current[currentIndex]?.scaleTo({
          x: 0,
          y: 0,
          scale: 1,
          animated: false,
        });
        setCurrentIndex(event.nextItem.index);
      }
    });

    const hasNext = currentIndex < items.length - 1;
    const hasPrev = currentIndex > 0;

    return (
      <ErrorBoundary>
        <ImageSliderWrapper newPdDesign={newPdDesign}>
          {hasPrev && (
            <StyledArrow
              direction="left"
              newPdDesign={newPdDesign}
              onClick={() => {
                slideToPrevItem();
                trackSwiping('button');
              }}
            />
          )}

          {carouselFragment}

          <ProgressBar count={items.length} index={currentIndex} />

          {hasNext && (
            <StyledArrow
              data-testid="chevron-right"
              direction="right"
              newPdDesign={newPdDesign}
              onClick={() => {
                slideToNextItem();
                trackSwiping('button');
              }}
            />
          )}
        </ImageSliderWrapper>
      </ErrorBoundary>
    );
  }
);

type QuickPinchZoomProps = React.ComponentProps<typeof QuickPinchZoom>;
type ZoomableProps = Pick<QuickPinchZoomProps, 'onUpdate' | 'children'> & {
  zoomRef: (ref: QuickPinchZoom) => void;
  divRef: (el: HTMLDivElement) => void;
};

const Zoomable = ({ children, onUpdate, zoomRef, divRef }: ZoomableProps) => {
  const { mobileGalleryZoomEnabled } = useFeatureFlags();

  if (!mobileGalleryZoomEnabled) {
    return children;
  }

  return (
    <ZoomWrapper>
      <QuickPinchZoom
        onUpdate={onUpdate}
        minZoom={1}
        maxZoom={2}
        enforceBoundsDuringZoom={true}
        tapZoomFactor={0}
        ref={zoomRef}
      >
        <div ref={divRef} style={{ width: '100%' }}>
          {children}
        </div>
      </QuickPinchZoom>
    </ZoomWrapper>
  );
};
